import React, { useMemo, useState } from "react";
import { FaFileAlt } from "react-icons/fa";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import { useNavigateToGivenPath } from "views/pages/manage/edpOps/studentDetails/paymentSummary/service";
import { updateRequest } from "views/pages/manage/common/utils/methods/commonMethods/utilityMethod";

import CashLocationCard from "../components/CashLocationCard";
import FilterDrawer from "../components/CashLocationFilterDrawer";
import TablePagination from "../components/TablePagination";
import TipsSidebar from "../components/TipsSidebar";
import ModuleHeaderBar from "../components/TopHeaderBar";
import ALoader from "views/pages/manage/common/a-loader";

import { useCashLocations } from "../hooks/useCashLocations";
import { useFetchDropdown } from "../hooks/useDropdown";
import { downloadCashReport } from "../api";
import { getLocationWithDescription } from "modules/paymentManagement/utils";

import "../styles/cashManagement.scss";
import { isLiveBranch } from "../../utils";
import { dashBoardFilterBtnStyle } from "../../cheque-management/utils";
import { checkPermission } from "views/pages/manage/common/utils/methods/commonMethods/utilityMethod";
import { useGetPermissions } from "layouts/customHook";

const CashManagementDashboard = () => {
  const [isDropdownLoading, setIsDropdownLoading] = useState(false);
  const { dropdownValues, isDropdownApiLoading } =
    useFetchDropdown(setIsDropdownLoading);
  const userPermissions = useGetPermissions('cashPaymentManagement')
  const isWritePermission = checkPermission(userPermissions, "C")||checkPermission(userPermissions, "U");

  const liveBranches = dropdownValues.businessAreas.filter(isLiveBranch);
  const {
    rows: locations,
    loading,
    total,
    pageNo,
    totalPages,
    pageSize,
    nextButtonHandler,
    applyFilter,
    filters,
  } = useCashLocations(
    { pageNo: 0, pageSize: 10 },
    { enabled: isDropdownLoading },
    liveBranches,
    "CASH"
  );

  const navigate = useNavigateToGivenPath();

  const locationWithDescription = getLocationWithDescription(
    locations,
    dropdownValues.businessAreas
  );
  const filterCount = useMemo(
    () =>
      Object.values(filters).filter(
        (item) => item.length > 0 || (!Array.isArray(item) && item !== "")
      )?.length,
    [filters]
  );

  const pagination = {
    total,
    page: pageNo,
    pageSize,
    totalPages,
  };

  const applyFilterHandler = (currentFilter) => {
    const updatedFilter = updateRequest(currentFilter);
    applyFilter(updatedFilter);
  };

  const onRemoveFilterHandler = () => {
    applyFilter({});
  };
  const onPageChange = (page) => {
    nextButtonHandler(page, pagination.pageSize);
  };

  const goViewReceipts = (locationId, cashierOffice) =>
    navigate(
      `/admin/cash-management/viewReceipt/${locationId}/${cashierOffice}`
    );

  const goViewDeposit = (locationId, cashierOffice) =>
    navigate(
      `/admin/cash-management/viewCashDepositReceipt/${locationId}/${cashierOffice}`
    );

  const goViewDepositPage = (locationId, cashierOffice) =>
    navigate(`/admin/cash-management/viewCashDeposit/${locationId}`);

  const onPageSizeChangeHandler = (pageSize) => {
    nextButtonHandler(0, pageSize);
  };

  if (!isDropdownApiLoading && liveBranches.length === 0) {
    return (
      <div className="display-2">
        This feature is not live yet on your branch
      </div>
    );
  }

  return (
    <div className="remove-container-top-space">
      <ModuleHeaderBar
        onBackClick={() => window.history.back()}
        title="Cash Management"
      >
        <div>
          <FilterDrawer
            appliedForm={filters}
            onRemoveFilterHandler={onRemoveFilterHandler}
            applyFilterHandler={applyFilterHandler}
            dropdownValues={dropdownValues}
            filterCount={filterCount}
            liveBranches={liveBranches}
          />
          <AButton
            size="xs"
            variant="general"
            disabled={!filterCount}
            updatedStyle={dashBoardFilterBtnStyle}
            onClick={() => downloadCashReport(filters)}
          >
            <div className="cm-card-btn">
              <FaFileAlt />
              Cash report
            </div>
          </AButton>
        </div>
      </ModuleHeaderBar>
      <div className="cm-container">
        {(loading || isDropdownApiLoading) && (
          <ALoader position="fixed"></ALoader>
        )}
        {!loading && !isDropdownApiLoading && locations.length === 0 && (
          <div className="text-center">No locations found</div>
        )}

        <div className="cm-grid">
          {/* Left side – list of cards */}
          <div className="cm-cards">
            {locationWithDescription.map((loc) => (
              <CashLocationCard
                key={loc.businessAreaValue}
                location={loc}
                onViewReceipts={goViewDeposit}
                onSendDeposit={goViewReceipts}
                onViewDeposit={goViewDepositPage}
                onViewReport={() => {}}
                isWritePermission={isWritePermission}
              />
            ))}

            {/* Pagination */}
            {totalPages > 0 && (
              <TablePagination
                loading={loading}
                pagination={pagination}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChangeHandler}
              />
            )}
          </div>

          {/* Right side – Tips panel */}
          <TipsSidebar />
        </div>
      </div>
    </div>
  );
};

export default CashManagementDashboard;
